import { useEffect, useState } from "react";
import { api, fmtEur, fmtDate, downloadFile } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { TrendingUp, Wallet, Coins, Percent, Receipt, FileSpreadsheet, FileText, HandCoins, AlertTriangle, Presentation, Video, MonitorPlay, Briefcase, CalendarDays, MapPin, LayoutGrid, Megaphone, X } from "lucide-react";

const PIE_COLORS = ["#4C728F", "#5B8FA8", "#7BA3BF", "#9CBBD1", "#C3D7E6", "#2C4355"];

const CATEGORIE = [
  { key: "aula", label: "Corsi Aula", icon: Presentation, cls: "bg-dusty-50 text-dusty-700 border-dusty-200", bar: "bg-dusty-500" },
  { key: "videoconferenza", label: "Corsi VideoConferenza", icon: Video, cls: "bg-violet-50 text-violet-700 border-violet-200", bar: "bg-violet-500" },
  { key: "elearning", label: "Corsi E-learning", icon: MonitorPlay, cls: "bg-teal-50 text-teal-700 border-teal-200", bar: "bg-teal-500" },
  { key: "consulenza", label: "Consulenza Aziendale", icon: Briefcase, cls: "bg-amber-50 text-amber-700 border-amber-200", bar: "bg-amber-500" },
];

function CategorieErogazione({ catalog, onGo }) {
  const total = catalog.length;
  return (
    <Card className="p-5 border-slate-200 shadow-sm" data-testid="categorie-erogazione">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-semibold text-slate-800 flex items-center gap-2"><LayoutGrid className="h-5 w-5 text-dusty-600" /> Categorie di erogazione</h3>
        <span className="text-xs text-slate-400">{total} corsi in catalogo</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        {CATEGORIE.map((c) => {
          const n = catalog.filter((s) => (s.categoria || "aula") === c.key).length;
          const pct = total ? Math.round((n / total) * 100) : 0;
          return (
            <button key={c.key} onClick={() => onGo(c.key)} data-testid={`cat-tile-${c.key}`}
              className={`text-left rounded-xl border p-4 hover:shadow-md transition-all ${c.cls}`}>
              <div className="flex items-start justify-between">
                <span className="h-9 w-9 rounded-lg bg-white/70 flex items-center justify-center"><c.icon className="h-5 w-5" /></span>
                <span className="text-3xl font-bold leading-none">{n}</span>
              </div>
              <div className="font-semibold text-sm mt-3 leading-tight">{c.label}</div>
              <div className="mt-2 h-1.5 rounded-full bg-white/60 overflow-hidden">
                <div className={`h-full ${c.bar} transition-all`} style={{ width: `${pct}%` }} />
              </div>
              <div className="mt-1 text-[11px] opacity-70">{pct}% del catalogo</div>
            </button>
          );
        })}
      </div>
    </Card>
  );
}

function StatCard({ id, title, value, icon: Icon, tint }) {
  return (
    <Card className="p-5 border-slate-200 shadow-sm" data-testid={id}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase font-semibold tracking-wider text-slate-500">{title}</div>
          <div className="mt-2 font-display text-2xl font-bold text-slate-900">{value}</div>
        </div>
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${tint}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const isOwner = user.role === "owner";
  const isCollab = user.role === "collaboratore";
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [dal, setDal] = useState("");
  const [al, setAl] = useState("");
  const [catalog, setCatalog] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [announcement, setAnnouncement] = useState({ testo: "", attivo: false });
  const [annEdit, setAnnEdit] = useState({ open: false, testo: "", busy: false });

  const loadAnnouncement = () => api.get("/announcement").then((r) => setAnnouncement(r.data)).catch(() => {});
  useEffect(() => { loadAnnouncement(); }, []);

  const publishAnnouncement = async (attivo) => {
    setAnnEdit((s) => ({ ...s, busy: true }));
    try {
      const r = await api.put("/announcement", { testo: annEdit.testo, attivo });
      setAnnouncement(r.data);
      setAnnEdit({ open: false, testo: r.data.testo, busy: false });
      toast.success(attivo ? "Avviso pubblicato" : "Avviso rimosso");
    } catch { toast.error("Errore nel salvataggio dell'avviso"); setAnnEdit((s) => ({ ...s, busy: false })); }
  };
  const openAnnEdit = () => setAnnEdit({ open: true, testo: announcement.testo || "", busy: false });

  const load = () => {
    const params = {};
    if (dal) params.dal = dal;
    if (al) params.al = al;
    api.get("/dashboard", { params }).then((r) => setData(r.data));
  };
  useEffect(load, [dal, al]);

  useEffect(() => {
    api.get("/services").then((r) => setCatalog(r.data)).catch(() => {});
    if (!isOwner) {
      const today = new Date().toISOString().slice(0, 10);
      api.get("/events", { params: { dal: today } }).then((r) => setUpcoming(r.data.slice(0, 6))).catch(() => {});
    }
  }, [isOwner]);

  const exportSettlement = async (fmt) => {
    const params = new URLSearchParams();
    if (dal) params.set("dal", dal);
    if (al) params.set("al", al);
    const qs = params.toString();
    const ext = fmt === "excel" ? "xlsx" : fmt;
    try {
      await downloadFile(`/reports/partner-settlement/${fmt}${qs ? `?${qs}` : ""}`, `conteggio_partner.${ext}`);
    } catch { toast.error("Errore durante l'esportazione"); }
  };

  if (!data) return <div className="text-slate-400">Caricamento…</div>;
  const t = data.totali;

  const banner = announcement.attivo && announcement.testo ? (
    <div className="rounded-xl border-2 border-rose-300 bg-rose-600 text-white px-5 py-4 shadow-sm animate-fade-up" data-testid="announcement-banner">
      <div className="flex items-start gap-3">
        <Megaphone className="h-6 w-6 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-wider text-rose-100">Comunicazione importante</div>
          <div className="font-semibold whitespace-pre-wrap leading-snug mt-0.5">{announcement.testo}</div>
        </div>
      </div>
    </div>
  ) : null;

  const ownerAnnEditor = isOwner ? (
    <Card className="p-4 border-slate-200 shadow-sm" data-testid="announcement-editor">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center"><Megaphone className="h-5 w-5" /></div>
          <div>
            <div className="font-display font-semibold text-slate-800">Avviso in bacheca</div>
            <div className="text-xs text-slate-500">{announcement.attivo ? "Avviso attualmente visibile a tutti in dashboard." : "Nessun avviso attivo."}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!annEdit.open && (
            <Button variant="outline" size="sm" onClick={openAnnEdit} data-testid="announcement-edit-btn">
              {announcement.attivo ? "Modifica avviso" : "Pubblica avviso"}
            </Button>
          )}
          {announcement.attivo && !annEdit.open && (
            <Button variant="outline" size="sm" onClick={() => publishAnnouncement(false)} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="announcement-remove-btn">
              <X className="h-4 w-4 mr-1" /> Rimuovi
            </Button>
          )}
        </div>
      </div>
      {annEdit.open && (
        <div className="mt-3 space-y-2">
          <Textarea value={annEdit.testo} onChange={(e) => setAnnEdit((s) => ({ ...s, testo: e.target.value }))} rows={3} placeholder="Scrivi qui la comunicazione (es. aggiornamento urgente, chiusura, emergenza)…" data-testid="announcement-textarea" />
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => publishAnnouncement(true)} disabled={annEdit.busy || !annEdit.testo.trim()} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="announcement-publish-btn">{annEdit.busy ? "Salvo…" : "Pubblica in rosso"}</Button>
            <Button size="sm" variant="ghost" onClick={() => setAnnEdit({ open: false, testo: "", busy: false })}>Annulla</Button>
          </div>
        </div>
      )}
    </Card>
  ) : null;

  if (isCollab) {
    return (
      <div className="space-y-8 animate-fade-up">
        {banner}
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Panoramica Operativa</h1>
          <p className="text-sm text-slate-500 mt-1">Corsi, ordini e attività. L'area contabilità e finanza è riservata alla direzione.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard id="kpi-vendite" title="Numero Vendite" value={t.num_vendite} icon={Receipt} tint="bg-dusty-50 text-dusty-600" />
        </div>
        <CategorieErogazione catalog={catalog} onGo={() => navigate("/servizi")} />
      </div>
    );
  }

  const debitori = isOwner ? (data.saldo_partner || []).filter((p) => p.da_dare < 0) : [];

  return (
    <div className="space-y-8 animate-fade-up">
      {banner}
      {ownerAnnEditor}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">
            {isOwner ? "Panoramica Finanziaria" : "Le Tue Royalties"}
          </h1>
          {!isOwner ? (
            <div className="mt-2 flex items-center gap-2" data-testid="partner-name-badge">
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Partner</span>
              <span className="text-xl font-bold text-dusty-700">{user.nome}</span>
            </div>
          ) : (
            <p className="text-sm text-slate-500 mt-1">Riepilogo di vendite, royalties e utile netto.</p>
          )}
        </div>
        <div className="flex items-end gap-3">
          <div>
            <Label className="text-xs text-slate-500">Dal</Label>
            <Input type="date" value={dal} onChange={(e) => setDal(e.target.value)} data-testid="filter-dal" className="mt-1 bg-white h-9" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Al</Label>
            <Input type="date" value={al} onChange={(e) => setAl(e.target.value)} data-testid="filter-al" className="mt-1 bg-white h-9" />
          </div>
        </div>
      </div>

      {isOwner && debitori.length > 0 && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4" data-testid="alert-saldo-negativo">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center shrink-0"><AlertTriangle className="h-5 w-5" /></div>
            <div className="flex-1">
              <div className="font-semibold text-rose-800">Partner con saldo a debito verso la Safe</div>
              <p className="text-sm text-rose-700/80 mt-0.5">Questi partner hanno incassato più delle royalty maturate e devono restituire denaro alla Safe{(dal || al) ? " nel periodo selezionato" : ""}.</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {debitori.map((p) => (
                  <span key={p.partner_id} data-testid={`debitore-${p.partner_id}`} className="inline-flex items-center gap-1.5 text-sm bg-white border border-rose-200 rounded-full px-3 py-1">
                    <span className="font-medium text-slate-700">{p.nome}</span>
                    <span className="font-bold text-rose-600">{fmtEur(Math.abs(p.da_dare))}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={`grid grid-cols-1 sm:grid-cols-2 ${isOwner ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-4`}>
        <StatCard id="kpi-fatturato" title={isOwner ? "Fatturato Totale" : "Le Tue Vendite"} value={fmtEur(t.fatturato)} icon={TrendingUp} tint="bg-dusty-50 text-dusty-600" />        {isOwner && <StatCard id="kpi-utile" title="Utile Netto" value={fmtEur(t.utile_netto)} icon={Wallet} tint="bg-emerald-50 text-emerald-600" />}
        <StatCard id="kpi-royalty" title={isOwner ? "Royalty Totali" : "Royalty Maturate"} value={fmtEur(t.royalty)} icon={Coins} tint="bg-amber-50 text-amber-600" />
        <StatCard id="kpi-iva" title="IVA Calcolata" value={fmtEur(t.iva)} icon={Percent} tint="bg-slate-100 text-slate-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <StatCard id="kpi-vendite" title="Numero Vendite" value={t.num_vendite} icon={Receipt} tint="bg-dusty-50 text-dusty-600" />
        <StatCard id="kpi-incassato" title={isOwner ? "Incassato" : "Royalty Incassate"} value={fmtEur(t.incassato)} icon={Wallet} tint="bg-emerald-50 text-emerald-600" />
        <StatCard id="kpi-da-incassare" title={isOwner ? "Da Incassare" : "Royalty da Incassare"} value={fmtEur(t.da_incassare)} icon={Coins} tint="bg-rose-50 text-rose-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card className="p-5 lg:col-span-2 border-slate-200 shadow-sm" data-testid="chart-per-mese">
          <h3 className="font-display font-semibold text-slate-800 mb-4">Andamento Mensile</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.per_mese}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="mese" stroke="#94a3b8" fontSize={12} />
              <YAxis stroke="#94a3b8" fontSize={12} />
              <Tooltip formatter={(v) => fmtEur(v)} />
              <Legend />
              <Bar dataKey="fatturato" name="Fatturato" fill="#4C728F" radius={[4, 4, 0, 0]} />
              {isOwner
                ? <Bar dataKey="utile_netto" name="Utile Netto" fill="#10b981" radius={[4, 4, 0, 0]} />
                : <Bar dataKey="royalty" name="Royalty" fill="#f59e0b" radius={[4, 4, 0, 0]} />}
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5 border-slate-200 shadow-sm" data-testid="chart-per-servizio">
          <h3 className="font-display font-semibold text-slate-800 mb-4">Ripartizione per Servizio</h3>
          {data.per_servizio.length === 0 ? (
            <div className="text-sm text-slate-400 py-16 text-center">Nessun dato</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={data.per_servizio} dataKey="fatturato" nameKey="nome" cx="50%" cy="50%" outerRadius={90}>
                  {data.per_servizio.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmtEur(v)} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      {isOwner && <CategorieErogazione catalog={catalog} onGo={() => navigate("/servizi")} />}

      {isOwner && (
        <Card className="p-5 border-slate-200 shadow-sm" data-testid="table-per-partner">
          <h3 className="font-display font-semibold text-slate-800 mb-4">Riepilogo per Partner</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-4">Partner</th>
                  <th className="py-2 pr-4">Vendite</th>
                  <th className="py-2 pr-4">Fatturato</th>
                  <th className="py-2 pr-4">Royalty Maturate</th>
                </tr>
              </thead>
              <tbody>
                {data.per_partner.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2.5 pr-4 font-medium text-slate-800">{p.nome}</td>
                    <td className="py-2.5 pr-4 text-slate-600">{p.count}</td>
                    <td className="py-2.5 pr-4 text-slate-600">{fmtEur(p.fatturato)}</td>
                    <td className="py-2.5 pr-4 font-semibold text-amber-700">{fmtEur(p.royalty)}</td>
                  </tr>
                ))}
                {data.per_partner.length === 0 && (
                  <tr><td colSpan={4} className="py-8 text-center text-slate-400">Nessuna vendita registrata</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {isOwner ? (
        <Card className="p-5 border-slate-200 shadow-sm" data-testid="table-conteggio-partner">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h3 className="font-display font-semibold text-slate-800 flex items-center gap-2"><HandCoins className="h-5 w-5 text-dusty-600" /> Conteggio da dare ai Partner {(dal || al) && <span className="text-xs text-slate-400 font-normal">· periodo selezionato</span>}</h3>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => exportSettlement("excel")} data-testid="settlement-excel"><FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel</Button>
              <Button variant="outline" size="sm" onClick={() => exportSettlement("pdf")} data-testid="settlement-pdf"><FileText className="h-4 w-4 mr-1.5" /> PDF</Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-4">Partner</th>
                  <th className="py-2 pr-4">Tipo</th>
                  <th className="py-2 pr-4 text-right">Royalty / Quota ICS</th>
                  <th className="py-2 pr-4 text-right">Incassato dal partner</th>
                  <th className="py-2 pr-4 text-right">Da dare ora</th>
                  <th className="py-2 pr-4 text-right">In sospeso</th>
                </tr>
              </thead>
              <tbody>
                {data.saldo_partner.map((p) => (
                  <tr key={p.partner_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`settlement-row-${p.partner_id}`}>
                    <td className="py-2.5 pr-4 font-medium text-slate-800">{p.nome}</td>
                    <td className="py-2.5 pr-4">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${p.tipo === "rivenditore" ? "bg-violet-50 text-violet-700 border-violet-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>{p.tipo === "rivenditore" ? "Rivenditore" : "Intermediario"}</span>
                    </td>
                    <td className="py-2.5 pr-4 text-right text-amber-700">{p.tipo === "rivenditore" ? fmtEur(p.quota_ics) : fmtEur(p.royalty)}</td>
                    <td className="py-2.5 pr-4 text-right text-slate-600">{fmtEur(p.incassato_partner)}</td>
                    <td className={`py-2.5 pr-4 text-right font-bold ${p.da_dare < 0 ? "text-rose-600" : "text-emerald-700"}`}>{p.da_dare < 0 ? `${fmtEur(Math.abs(p.da_dare))} → ${p.tipo === "rivenditore" ? "a ICS" : "a Safe"}` : fmtEur(p.da_dare)}</td>
                    <td className="py-2.5 pr-4 text-right text-slate-400" data-testid={`settlement-sospeso-${p.partner_id}`}>
                      {(p.tipo === "rivenditore" ? p.quota_ics_sospeso : p.royalty_sospeso) > 0
                        ? <span title={`${p.n_sospese} vendite non ancora saldate`}>{fmtEur(p.tipo === "rivenditore" ? p.quota_ics_sospeso : p.royalty_sospeso)}{p.n_sospese ? ` · ${p.n_sospese}` : ""}</span>
                        : "—"}
                    </td>
                  </tr>
                ))}
                {data.saldo_partner.length === 0 && (
                  <tr><td colSpan={6} className="py-8 text-center text-slate-400">Nessun dato nel periodo</td></tr>
                )}
              </tbody>
              {data.saldo_partner.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-slate-200 font-semibold">
                    <td className="py-2.5 pr-4 text-slate-700" colSpan={2}>Totale da dare ai partner (Safe → Partner)</td>
                    <td></td><td></td>
                    <td className="py-2.5 pr-4 text-right text-emerald-700" data-testid="settlement-totale">{fmtEur(data.totale_da_ricevere)}</td>
                    <td className="py-2.5 pr-4 text-right text-slate-400" data-testid="settlement-totale-sospeso">{fmtEur(data.totale_sospeso)}</td>
                  </tr>
                  {data.totale_deve_a_struttura > 0 && (
                    <tr className="font-semibold bg-rose-50/60">
                      <td className="py-2.5 pr-4 text-rose-700" colSpan={2}>Totale dovuto dai partner alla struttura (Partner → Safe/ICS)</td>
                      <td></td><td></td>
                      <td className="py-2.5 pr-4 text-right text-rose-600" data-testid="settlement-totale-a-struttura">{fmtEur(data.totale_deve_a_struttura)}</td>
                      <td></td>
                    </tr>
                  )}
                </tfoot>
              )}
            </table>
          </div>
          <p className="text-xs text-slate-400 mt-3">Conteggio sulle vendite già <b>saldate</b> («Pagato»); «In sospeso» entra quando le segni «Pagato». <b>Intermediario</b>: «Da dare ora» = royalty − incassato dal partner (negativo = il partner deve a Safe). <b>Rivenditore</b>: incassa dai clienti e deve a ICS la quota (metà prezzo + IVA), mostrata come importo «a ICS».</p>
        </Card>
      ) : (
        <>
        <CategorieErogazione catalog={catalog} onGo={() => navigate("/catalogo")} />

        <Card className="p-5 border-slate-200 shadow-sm" data-testid="partner-upcoming">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold text-slate-800 flex items-center gap-2"><CalendarDays className="h-5 w-5 text-dusty-600" /> Prossimi corsi in programma</h3>
            <Button variant="outline" size="sm" onClick={() => navigate("/calendario")} data-testid="goto-calendar">Vedi calendario</Button>
          </div>
          {upcoming.length === 0 ? (
            <div className="text-sm text-slate-400 py-6 text-center">Nessun corso in programma al momento.</div>
          ) : (
            <div className="space-y-2">
              {upcoming.map((e) => (
                <div key={e.id} className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2" data-testid={`upcoming-${e.id}`}>
                  <div className="text-center min-w-[54px]">
                    <div className="text-xs text-slate-400">{fmtDate(e.data)}</div>
                    {e.ora_inizio && <div className="text-xs font-semibold text-slate-600">{e.ora_inizio}</div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-800 truncate">{e.servizio_nome}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-1">
                      {e.modalita === "videoconferenza" ? <Video className="h-3 w-3" /> : <MapPin className="h-3 w-3" />}
                      {e.modalita === "videoconferenza" ? "Videoconferenza" : "Aula"}{e.luogo ? ` · ${e.luogo}` : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-5 border-slate-200 shadow-sm" data-testid="partner-settlement-card">
          <h3 className="font-display font-semibold text-slate-800 mb-3 flex items-center gap-2"><HandCoins className="h-5 w-5 text-dusty-600" /> Conteggio con la Safe {(dal || al) && <span className="text-xs text-slate-400 font-normal">· periodo selezionato</span>}</h3>
          {(data.saldo_partner.length > 0 ? data.saldo_partner : [{ partner_id: "self", tipo: "intermediario", royalty: 0, incassato_partner: 0, da_dare: 0, quota_ics: 0, quota_ics_sospeso: 0, fatturato: 0 }]).map((p) => (
            p.tipo === "rivenditore" ? (
              <div key={p.partner_id} data-testid="partner-conteggio-rivenditore">
                <div className="text-[11px] uppercase tracking-wider text-violet-600 font-semibold mb-2">Rivenditore</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div><div className="text-xs text-slate-500">Incassato da te (dai clienti)</div><div className="font-bold text-slate-700 text-lg">{fmtEur(p.incassato_partner)}</div></div>
                  <div><div className="text-xs text-slate-500">Da versare a ICS (quota + IVA)</div><div className="font-bold text-violet-700 text-lg" data-testid="partner-quota-ics">{fmtEur(p.quota_ics)}</div></div>
                  <div><div className="text-xs text-slate-500">In sospeso (da saldare)</div><div className="font-bold text-amber-600 text-lg">{fmtEur(p.quota_ics_sospeso)}</div></div>
                </div>
                <div className="mt-3 rounded-lg bg-violet-50 border border-violet-200 px-3 py-2 text-sm text-violet-800 font-medium" data-testid="partner-saldo-frase">
                  {p.quota_ics > 0 ? `Devi versare a ICS ${fmtEur(p.quota_ics)} sui corsi già saldati.` : "Nessun importo da versare sui corsi già saldati."}
                  {p.quota_ics_sospeso > 0 ? ` In sospeso: ${fmtEur(p.quota_ics_sospeso)} (entrerà nel conteggio quando i corsi saranno segnati «Pagato»).` : ""}
                </div>
              </div>
            ) : (
              <div key={p.partner_id}>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div><div className="text-xs text-slate-500">Royalty maturata</div><div className="font-bold text-amber-700 text-lg">{fmtEur(p.royalty)}</div></div>
                  <div><div className="text-xs text-slate-500">Già incassato da te</div><div className="font-bold text-slate-700 text-lg">{fmtEur(p.incassato_partner)}</div></div>
                  <div><div className="text-xs text-slate-500">{p.da_dare < 0 ? "Da restituire alla Safe" : "Da ricevere dalla Safe"}</div><div className={`font-bold text-lg ${p.da_dare < 0 ? "text-rose-600" : "text-emerald-700"}`}>{fmtEur(Math.abs(p.da_dare))}</div></div>
                </div>
                <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm text-slate-700 font-medium" data-testid="partner-saldo-frase">
                  {p.da_dare < 0 ? `Devi restituire alla Safe ${fmtEur(Math.abs(p.da_dare))} (hai incassato più della royalty maturata).` : p.da_dare > 0 ? `La Safe ti deve ${fmtEur(p.da_dare)}.` : "Conteggio in pari."}
                </div>
              </div>
            )
          ))}
          <p className="text-xs text-slate-400 mt-3">Il conteggio considera solo le vendite già saldate («Pagato»); gli importi «in sospeso» entrano quando vengono segnati come pagati.</p>
        </Card>
        </>
      )}
    </div>
  );
}
